/*
  Two jobs, both for snippets/pf-rewards-bar.liquid:

  1. <pf-rewards> keeps a cart attribute in sync with the rewards the cart has
     earned, so the gift is visible on the order in the Shopify admin. It is a
     custom element rather than an inline script because the drawer and cart
     page are re-rendered with innerHTML, which never executes inline scripts
     but does upgrade custom elements.

  2. The compact product-page bar mirrors the drawer's bar and pins itself
     under the header: it hides on scroll-down, comes back on scroll-up, and
     settles into a badge once the shopper stops. The drawer's bar is the
     source of truth because Shopify re-renders it on every cart change, so
     it always carries the right totals and money format.
*/

class PfRewards extends HTMLElement {
  connectedCallback() {
    const name = this.dataset.attributeName;
    if (!name) return;

    const value = this.dataset.attributeValue || '';
    if (PfRewards.lastSynced.get(name) === value) return;

    // Claim the value up front so two bars on the same page (drawer + page)
    // cannot both fire for the same state.
    PfRewards.lastSynced.set(name, value);

    fetch(`${(window.routes && window.routes.cart_update_url) || '/cart/update'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [name]: value } }),
    }).catch(() => {
      PfRewards.lastSynced.delete(name);
    });
  }
}

PfRewards.lastSynced = new Map();

if (!customElements.get('pf-rewards')) {
  customElements.define('pf-rewards', PfRewards);
}

(() => {
  if (window.__pfRewardsPin) return;
  window.__pfRewardsPin = true;

  const STATES = ['pf-rewards--offer', 'pf-rewards--partial', 'pf-rewards--unlocked'];
  const SOURCE = '.pf-rewards--drawer';
  const PINNED = '[data-pf-rewards-pin]';

  /* ── mirror the drawer's bar ─────────────────────────────── */

  const copyFrom = (mirror, source) => {
    const message = mirror.querySelector('.pf-rewards__message');
    const sourceMessage = source.querySelector('.pf-rewards__message');
    if (message && sourceMessage) message.innerHTML = sourceMessage.innerHTML;

    const fill = source.querySelector('.pf-rewards__fill');
    const mirrorFill = mirror.querySelector('.pf-rewards__fill');
    if (mirrorFill && fill) mirrorFill.style.width = fill.style.width;

    const sourceTiers = source.querySelectorAll('.pf-rewards__tier');
    mirror.querySelectorAll('.pf-rewards__tier').forEach((tier, i) => {
      const from = sourceTiers[i];
      if (from) tier.classList.toggle('is-unlocked', from.classList.contains('is-unlocked'));
    });

    STATES.forEach((c) => mirror.classList.remove(c));
    mirror.classList.add(STATES.find((c) => source.classList.contains(c)) || 'pf-rewards--partial');
  };

  const resetToOffer = (mirror) => {
    const template = mirror.querySelector('template[data-pf-rewards-offer]');
    if (!template) return;

    const message = mirror.querySelector('.pf-rewards__message');
    if (message) message.innerHTML = template.innerHTML;

    const fill = mirror.querySelector('.pf-rewards__fill');
    if (fill) fill.style.width = '0%';

    mirror.querySelectorAll('.pf-rewards__tier').forEach((tier) => tier.classList.remove('is-unlocked'));

    STATES.forEach((c) => mirror.classList.remove(c));
    mirror.classList.add('pf-rewards--offer');
  };

  const sync = () => {
    const mirrors = document.querySelectorAll(PINNED);
    if (!mirrors.length) return;
    const source = document.querySelector(SOURCE);
    mirrors.forEach((mirror) => (source ? copyFrom(mirror, source) : resetToOffer(mirror)));
  };

  /* ── smart pinning ───────────────────────────────────────── */

  // Distance past the pin point before it tucks away. Without it the bar pins
  // and collapses in the same frame, because pinning IS a scroll-down.
  const COLLAPSE_AFTER = 220;
  // Long enough to finish reading the line before it settles.
  const IDLE_MS = 1200;

  let lastY = window.scrollY;
  let queued = false;
  let idleTimer;

  // header.liquid measures the sticky header into --header-height on load and
  // resize, so read that rather than the wrapper's box (which also covers the
  // announcement bar and would pin the bar too late).
  const headerOffset = () =>
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 62;

  const drawerOpen = () => !!document.querySelector('cart-drawer.active, cart-drawer.animate.active');

  const measure = () => {
    document.querySelectorAll('[data-pf-rewards-slot]').forEach((slot) => {
      const bar = slot.querySelector(PINNED);
      if (!bar || bar.dataset.pinned === 'true') return;
      // Lock the gap the bar leaves behind before it is taken out of flow.
      slot.style.minHeight = `${bar.offsetHeight}px`;
      const rect = slot.getBoundingClientRect();
      bar.style.setProperty('--pf-pin-left', `${rect.left}px`);
      bar.style.setProperty('--pf-pin-width', `${rect.width}px`);
    });
  };

  // Stopping is the cue to settle: still visible, just out of the way.
  const settle = () => {
    document.querySelectorAll(`[data-pf-rewards-slot] ${PINNED}`).forEach((bar) => {
      if (bar.dataset.pinned === 'true' && bar.dataset.hidden !== 'true') {
        bar.dataset.collapsed = 'true';
      }
    });
  };

  const onScroll = () => {
    const y = window.scrollY;
    const goingDown = y > lastY + 2;
    const goingUp = y < lastY - 2;
    const offset = headerOffset();

    document.querySelectorAll('[data-pf-rewards-slot]').forEach((slot) => {
      const bar = slot.querySelector(PINNED);
      if (!bar) return;

      const top = slot.getBoundingClientRect().top;
      const pin = top < offset;
      bar.dataset.pinned = pin ? 'true' : 'false';

      if (!pin) {
        bar.dataset.hidden = 'false';
        bar.dataset.collapsed = 'false';
        return;
      }

      // The drawer has a bar of its own, so this one steps aside.
      bar.dataset.hidden = drawerOpen() ? 'true' : 'false';

      const past = offset - top;
      if (goingUp) {
        bar.dataset.collapsed = 'false';
      } else if (goingDown && past > COLLAPSE_AFTER) {
        bar.dataset.collapsed = 'true';
      }
    });

    clearTimeout(idleTimer);
    idleTimer = setTimeout(settle, IDLE_MS);

    if (goingDown || goingUp) lastY = y;
    queued = false;
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(onScroll);
  };

  const start = () => {
    if (!document.querySelector(PINNED)) return;
    sync();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', () => { measure(); schedule(); }, { passive: true });
    requestAnimationFrame(() => { measure(); onScroll(); });
  };

  const onCartUpdate = () => requestAnimationFrame(() => { sync(); measure(); schedule(); });

  // Filtering rebuilds the collection grid under the bar, which moves the slot.
  const remeasure = () => requestAnimationFrame(() => { measure(); schedule(); });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }
  document.addEventListener('cart:update', onCartUpdate);
  window.addEventListener('popstate', remeasure);
  document.addEventListener('shopify:section:load', remeasure);

  // Inline modules can run either side of DOMContentLoaded, and a listener
  // added after it has fired never runs — so check the state first.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
